<template>
  <div class="account-assistants" v-on-clickaway="dismiss" @keydown.esc.stop="close">
    <section v-if="open" id="account-assistants-panel" class="account-assistants__panel" role="dialog" aria-labelledby="account-assistants-title">
      <div class="account-assistants__heading">
        <div>
          <h2 id="account-assistants-title">Your assistants</h2>
          <p>{{ canOptimize ? 'Configure your account and improve your LSA profile.' : 'Configure your account and follow the next setup step.' }}</p>
        </div>
        <button ref="closeButton" class="account-assistants__close" type="button" aria-label="Close assistants" @click="close">
          <i class="mdi mdi-close" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="accountName" class="account-assistants__account">
        <i class="mdi mdi-briefcase-outline" aria-hidden="true"></i>
        <span>{{ accountName }}</span>
      </div>

      <div class="account-assistants__body">
        <div class="account-assistants__agent">
          <div class="account-assistants__icon"><i class="mdi mdi-clipboard-check-outline" aria-hidden="true"></i></div>
          <div class="account-assistants__details">
            <h3>Setup Agent <span class="account-assistants__tag">Account settings</span></h3>
            <p>Set up your account, change settings and follow the next step.</p>
            <router-link :to="setupRoute" class="account-assistants__link" @click.native="dismiss">
              Open Setup Agent <i class="mdi mdi-arrow-right" aria-hidden="true"></i>
            </router-link>
          </div>
        </div>

        <div v-if="canOptimize" class="account-assistants__agent account-assistants__agent--planned">
          <div class="account-assistants__icon account-assistants__icon--planned"><i class="mdi mdi-chart-line" aria-hidden="true"></i></div>
          <div class="account-assistants__details">
            <h3>LSA Optimization Agent</h3>
            <p>Use your LSA Audit to identify and work through profile improvements.</p>
            <router-link :to="optimizationRoute" class="account-assistants__link" @click.native="dismiss">Open LSA Optimization Agent <i class="mdi mdi-arrow-right" aria-hidden="true"></i></router-link>
          </div>
        </div>
      </div>
    </section>

    <button ref="launcher" class="account-assistants__launcher" type="button" :aria-expanded="String(open)" aria-controls="account-assistants-panel" aria-haspopup="dialog" @click="toggle">
      <i :class="open ? 'mdi mdi-close' : 'mdi mdi-robot'" aria-hidden="true"></i>
      <span>Assistants</span>
    </button>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

export default {
  name: 'AccountAssistants',
  mixins: [clickaway],
  props: { account: { type: Object, default: null } },
  data() { return { open: false }; },
  computed: {
    canOptimize() {
      return !!this.account && ['business', 'location'].includes(this.account.company_type);
    },
    accountName() {
      return this.account && (this.account.company_name || this.account.name) || '';
    },
    optimizationRoute() { return { path: '/optimization', query: this.account && this.account.id ? { account: String(this.account.id) } : {} }; },
    setupRoute() {
      return { path: '/onboarding', query: this.canOptimize && this.account.id ? { account: String(this.account.id) } : {} };
    }
  },
  watch: {
    '$route.fullPath'() { this.dismiss(); },
    'account.id'() { this.dismiss(); }
  },
  methods: {
    toggle() {
      if (this.open) return this.close();
      this.open = true;
      this.$nextTick(() => { if (this.$refs.closeButton) this.$refs.closeButton.focus(); });
    },
    dismiss() { this.open = false; },
    close() {
      this.dismiss();
      if (this.$refs.launcher) this.$refs.launcher.focus();
    }
  }
};
</script>

<style scoped>
.account-assistants { position: fixed; right: 15px; bottom: 15px; z-index: 8; font-size: 13px; line-height: 1.5; color: #19233a; }
.account-assistants__launcher { display: flex; align-items: center; gap: 8px; min-height: 46px; padding: 0 16px; border: 1px solid #dce5e9; border-radius: 8px; background: #fff; color: #19233a; font-weight: 700; box-shadow: 0 3px 14px rgba(25, 35, 58, .12); }
.account-assistants__launcher > i { font-size: 23px; color: #279a7b; }
.account-assistants__launcher:hover { background: #f5fbf9; border-color: #4ec5a1; }
.account-assistants__launcher:focus-visible, .account-assistants__close:focus-visible, .account-assistants__link:focus-visible { outline: 2px solid #279a7b; outline-offset: 3px; }
.account-assistants__panel { position: absolute; right: 0; bottom: 60px; width: 360px; max-width: calc(100vw - 32px); max-height: calc(100vh - 96px); overflow-y: auto; border: 1px solid #e2e7ec; border-radius: 10px; background: #fff; box-shadow: 0 8px 32px rgba(25, 35, 58, .15); }
.account-assistants__heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 20px 20px 16px; }
.account-assistants__heading h2 { margin: 0; font-size: 17px; line-height: 1.5; font-weight: 700; color: #19233a; }
.account-assistants__heading p { margin: 4px 0 0; font-size: 12px; color: #6b7280; }
.account-assistants__close { width: 28px; height: 28px; flex-shrink: 0; border-radius: 4px; color: #6b7280; font-size: 20px; }
.account-assistants__close:hover { background: #f3f5f7; }
.account-assistants__account { display: flex; align-items: center; gap: 8px; margin: 0 20px 4px; padding: 9px 12px; border-radius: 5px; background: #f4f6f8; color: #596474; font-size: 12px; }
.account-assistants__account span { overflow-wrap: anywhere; }
.account-assistants__body { padding: 4px 20px; }
.account-assistants__agent { display: flex; align-items: flex-start; gap: 12px; padding: 20px 0; }
.account-assistants__agent + .account-assistants__agent { border-top: 1px solid #edf0f3; }
.account-assistants__icon { display: flex; align-items: center; justify-content: center; width: 38px; height: 38px; flex-shrink: 0; border-radius: 8px; background: #e9f7f1; color: #279a7b; font-size: 22px; }
.account-assistants__icon--planned { background: #f1f3f6; color: #788394; }
.account-assistants__details { min-width: 0; }
.account-assistants__details h3 { margin: 0; display: flex; align-items: center; flex-wrap: wrap; gap: 7px; font-size: 14px; font-weight: 700; line-height: 1.5; color: #19233a; }
.account-assistants__tag { padding: 2px 6px; background: #e9f7f1; border-radius: 4px; color: #247f66; font-size: 10px; font-weight: 500; }
.account-assistants__details p { margin: 6px 0 10px; color: #6b7280; font-size: 12px; }
.account-assistants__details .account-assistants__link { display: inline-flex; align-items: center; gap: 6px; color: #247f66 !important; font-size: 12px; font-weight: 700; text-decoration: none; }
.account-assistants__link:hover { text-decoration: underline; }
.account-assistants__planned { display: inline-block; padding: 3px 7px; border-radius: 4px; background: #f1f3f6; color: #596474; font-size: 11px; }
@media (max-width: 600px) {
  .account-assistants { right: 15px; }
  .account-assistants__launcher { padding: 0 12px; }
  .account-assistants__panel { right: 0; }
}
</style>
